export let signaturePadInstance = null;
let blankData = null;

// Utility: true when an element has a real layout box
function hasBox(el) {
  if (!el) return false;
  const r = el.getBoundingClientRect();
  return r.width > 0 && r.height > 0;
}

// Utility: (re)size canvas only when it has non-zero CSS size
function safeResizeCanvas(canvas) {
  const parent = canvas.parentElement;
  // Prefer parent box when available (common when canvas is 100% width)
  const cssW = (parent?.clientWidth ?? canvas.clientWidth) || 0;
  const cssH = (parent?.clientHeight ?? canvas.clientHeight) || 0;

  if (cssW === 0 || cssH === 0) return false;

  const ratio = Math.max(window.devicePixelRatio || 1, 1);

  // Only recalc backing store if dimensions changed
  const targetW = Math.floor(cssW * ratio);
  const targetH = Math.floor(cssH * ratio);
  if (canvas.width !== targetW || canvas.height !== targetH) {
    canvas.width  = targetW;
    canvas.height = targetH;

    const ctx = canvas.getContext("2d", { willReadFrequently: true });
    // reset then scale, so existing transforms don't compound
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(ratio, ratio);
  }
  return true;
}

// ONE blank snapshot into module-scoped `blankData`
function captureBlank(canvas) {
  // Avoid IndexSizeError when hidden / zero-sized
  if (!canvas || canvas.width === 0 || canvas.height === 0) {
    blankData = null; // force lazy recapture when visible
    return null;
  }

  // We create a same-size temp canvas and read its pixels (all transparent)
  const blankCanvas = document.createElement("canvas");
  blankCanvas.width  = canvas.width;
  blankCanvas.height = canvas.height;

  const blankCtx = blankCanvas.getContext("2d", { willReadFrequently: true });
  const img = blankCtx.getImageData(0, 0, canvas.width, canvas.height);
  blankData = img.data; // Uint8ClampedArray
  return blankData;
}

// Set up the pad + one-off blank snapshot
export function initSignaturePad(
  canvasId   = "signature-pad",
  clearBtnId = "clear-signature"
) {
  const canvas = document.getElementById(canvasId);
  if (!canvas) return;

  // If the canvas (or its container) is hidden, defer initialization.
  if (!hasBox(canvas)) {
    // Try again on the next frame; outer code also re-calls us on step change.
    requestAnimationFrame(() => initSignaturePad(canvasId, clearBtnId));
    return;
  }

  // Ensure backing store is sized to CSS box; bail if not ready yet.
  if (!safeResizeCanvas(canvas)) {
    requestAnimationFrame(() => initSignaturePad(canvasId, clearBtnId));
    return;
  }

  // initialize SignaturePad
  signaturePadInstance = new SignaturePad(canvas, {
    penColor:             "rgb(0, 0, 0)",
    velocityFilterWeight: 0.7,
    minWidth:             0.5,
    maxWidth:             2.5,
    throttle:             16, // ~60fps
  });

  // Initial blank snapshot (may be null if something hid it again)
  captureBlank(canvas);

  // Resize handler: only act when the canvas actually has size
  const onResize = () => {
    if (!safeResizeCanvas(canvas)) return;
    captureBlank(canvas);
  };

  // Use both window resize and a ResizeObserver for container changes
  window.addEventListener("resize", onResize);
  if (window.ResizeObserver) {
    const ro = new ResizeObserver(() => onResize());
    ro.observe(canvas.parentElement || canvas);
    // Keep a reference if you plan to disconnect later (optional).
    canvas._signatureResizeObserver = ro;
  }

  // Clear button also needs to recapture blank
  const clearBtn = document.getElementById(clearBtnId);
  if (clearBtn) {
    clearBtn.addEventListener("click", () => {
      signaturePadInstance.clear();
      captureBlank(canvas);
    });
  }

  // start from blank
  signaturePadInstance.clear();
}

// Pixel-based threshold, reusing blankData
export function isSignatureValid({ threshold = 0.01 } = {}) {
  const pad = signaturePadInstance;
  if (!pad) {
    console.error("SignaturePad not initialized.");
    return false;
  }

  const canvas = pad.canvas;
  // If canvas is hidden/zero, we can’t validate yet
  if (!canvas || canvas.width === 0 || canvas.height === 0) return false;

  const ctx  = canvas.getContext("2d", { willReadFrequently: true });
  const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data;

  // If we don’t have a blank snapshot or sizes changed, recapture now
  if (!blankData || blankData.length !== data.length) {
    captureBlank(canvas);
    // After recapture, still no blank? then defer
    if (!blankData || blankData.length !== data.length) return false;
  }

  const totalPix = data.length / 4;          // RGBA → 4 bytes/pixel
  const maxDiff  = threshold * totalPix;     // how many pixels must differ
  let diffCount  = 0;

  // Fast path: if pad reports empty, it’s empty
  if (signaturePadInstance.isEmpty()) return false;

  // Compare against blankData with early exit
  for (let i = 0; i < data.length; i += 4) {
    if (
      data[i]   !== blankData[i]   ||
      data[i+1] !== blankData[i+1] ||
      data[i+2] !== blankData[i+2] ||
      data[i+3] !== blankData[i+3]
    ) {
      diffCount++;
      if (diffCount > maxDiff) return true;
    }
  }
  return false;
}

export function getSignatureDataUrl() {
  const pad = signaturePadInstance;
  if (!pad) return null;
  const c = pad.canvas;
  if (!c || c.width === 0 || c.height === 0) return null;
  if (pad.isEmpty()) return null;
  return pad.toDataURL();
}

export function clearSignature() {
  if (signaturePadInstance) {
    signaturePadInstance.clear();
    const canvas = signaturePadInstance.canvas;
    captureBlank(canvas);
  }
}

export function isSignatureEmpty() {
  return signaturePadInstance?.isEmpty() ?? true;
}
